import React, { useState, useEffect, useCallback, useRef } from 'react';

type OAuthService = 'gsc' | 'ga4' | 'google_ads';

interface OAuthConnectButtonProps {
  service: OAuthService;
  label: string;
  onConnect: () => void;
  onDisconnect: () => void;
}

interface OAuthStatus {
  connected: boolean;
  email?: string;
}

const API_BASE = '/api/seotools/oauth';

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

function getUserIdFromToken(): string | null {
  const token = getToken();
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.user_id?.toString() ?? payload.sub?.toString() ?? null;
  } catch {
    return null;
  }
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const OAuthConnectButton: React.FC<OAuthConnectButtonProps> = ({
  service,
  label,
  onConnect,
  onDisconnect,
}) => {
  const [connected, setConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  // Check connection status
  const checkStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/status?service=${service}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Status check failed (${res.status})`);
      const data: OAuthStatus = await res.json();
      setConnected(data.connected);
      setConnectedEmail(data.email);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to check status';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const { type } = event.data || {};
      if (type === 'oauth-success') {
        setConnected(true);
        setActionLoading(false);
        popupRef.current?.close();
        popupRef.current = null;
        onConnect();
        checkStatus();
      } else if (type === 'oauth-error') {
        setError(event.data.message || 'OAuth authorization failed');
        setActionLoading(false);
        popupRef.current?.close();
        popupRef.current = null;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onConnect, checkStatus]);

  // Initiate OAuth connection
  const handleConnect = async () => {
    setError(null);
    setActionLoading(true);

    const userId = getUserIdFromToken();
    if (!userId) {
      setError('Not authenticated. Please log in again.');
      setActionLoading(false);
      return;
    }

    try {
      const callbackUrl = `${window.location.origin}/api/seotools/oauth/callback`;
      const params = new URLSearchParams({
        service,
        redirect_uri: callbackUrl,
        user_id: userId,
      });

      const res = await fetch(`${API_BASE}/auth-url?${params.toString()}`, {
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error(`Failed to get auth URL (${res.status})`);
      const data = await res.json();
      const authUrl = data.auth_url || data.url;

      if (!authUrl) throw new Error('No auth URL returned from server');

      // Open popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      popupRef.current = window.open(
        authUrl,
        `oauth_${service}`,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      // Poll to detect if user closed popup without completing
      const pollTimer = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(pollTimer);
          // Give a brief delay to allow postMessage to arrive first
          setTimeout(() => {
            setActionLoading(false);
          }, 500);
        }
      }, 1000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start OAuth flow';
      setError(msg);
      setActionLoading(false);
    }
  };

  // Disconnect
  const handleDisconnect = async () => {
    setError(null);
    setActionLoading(true);

    try {
      const res = await fetch(`${API_BASE}/disconnect?service=${service}`, {
        method: 'POST',
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error(`Disconnect failed (${res.status})`);

      setConnected(false);
      setConnectedEmail(undefined);
      onDisconnect();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to disconnect';
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm text-gray-500">Checking {label} connection...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-sm font-medium text-gray-700">{label}</span>

        {connected ? (
          <div className="flex items-center gap-2 ml-auto">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Connected{connectedEmail ? ` (${connectedEmail})` : ''}
            </span>
            <button
              onClick={handleDisconnect}
              disabled={actionLoading}
              className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <div className="ml-auto">
            <button
              onClick={handleConnect}
              disabled={actionLoading}
              className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600 px-1">{error}</p>
      )}
    </div>
  );
};

export default OAuthConnectButton;
